import type { BallWith, PillarKey, PracticeBand } from '../../api/portfolioApi';
import type { NotificationCategory, PracticeState } from '../../api/commandCenterApi';

/**
 * The three lanes of the Today queue. "Type" (action / review / fyi) describes
 * the shape of a piece of work; this describes whether it is yours to do, which
 * is what an operator actually sorts by.
 */
export const BALL_META: Record<BallWith, { label: string; lane: string; tone: string }> = {
  us: { label: 'Us', lane: 'Needs us', tone: 'error' },
  client: { label: 'Client', lane: 'Waiting on client', tone: 'warning' },
  auto: { label: 'Automated', lane: 'Running itself', tone: 'muted' },
};

/**
 * Where a practice is in its lifecycle. This is the column an operator scans
 * first — it says what the practice needs next, and unlike ad metrics it is
 * populated for every practice from day one.
 */
export const STATE_META: Record<PracticeState, { label: string; tone: string }> = {
  draft: { label: 'Draft', tone: 'muted' },
  invited: { label: 'Invited', tone: 'muted' },
  onboarding: { label: 'Onboarding', tone: 'info' },
  zero: { label: 'Nothing connected', tone: 'warning' },
  strategy: { label: 'Strategy only', tone: 'purple' },
  ads_only: { label: 'Ads only', tone: 'info' },
  seo_only: { label: 'SEO only', tone: 'info' },
  ads_seo: { label: 'Ads + SEO', tone: 'info' },
  active_30d: { label: 'Active', tone: 'success' },
  ongoing: { label: 'Ongoing', tone: 'success' },
  inactive: { label: 'Inactive', tone: 'muted' },
  suspended: { label: 'Suspended', tone: 'error' },
};

export const BAND_META: Record<PracticeBand, { label: string; tone: string }> = {
  risk: { label: 'At risk', tone: 'error' },
  setup: { label: 'Needs setup', tone: 'warning' },
  steady: { label: 'Steady', tone: 'success' },
  snoozed: { label: 'Snoozed', tone: 'muted' },
};

/** Weight and provenance for each health pillar, mirroring health.service.ts. */
export const PILLAR_META: Record<PillarKey, { label: string; weight: number; source: string }> = {
  strategy: { label: 'Strategy', weight: 15, source: 'Strategy status and wizard progress' },
  dataSignal: { label: 'Data', weight: 20, source: 'Integrations connected and last sync age' },
  campaigns: {
    label: 'Campaigns',
    weight: 30,
    source: 'Spend converting, CPA vs. this practice’s own 60-day baseline',
  },
  visibility: {
    label: 'Visibility',
    weight: 20,
    source: 'Latest site-audit score and search-click trend',
  },
  reputation: { label: 'Reputation', weight: 15, source: 'Rating, review velocity, reply rate' },
};

export const PILLAR_ORDER: PillarKey[] = [
  'strategy',
  'dataSignal',
  'campaigns',
  'visibility',
  'reputation',
];

export const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  onboarding: 'Setup',
  strategy: 'Strategy',
  visibility: 'Visibility',
  campaigns: 'Campaigns',
  alerts: 'Alerts',
};

export const CATEGORY_ORDER: NotificationCategory[] = [
  'onboarding',
  'strategy',
  'visibility',
  'campaigns',
  'alerts',
];

/** Health bands used for colouring a score. Matches deriveBand on the server. */
export function healthTone(score: number | null): 'success' | 'warning' | 'error' | 'muted' {
  if (score === null) return 'muted';
  if (score >= 70) return 'success';
  if (score >= 55) return 'warning';
  return 'error';
}

/**
 * How long an item has been waiting. Deliberately not a countdown: the SLA it
 * would count against was derived from a priority, never agreed with anyone,
 * so "3d overdue" asserted an obligation that does not exist. `overdue` here
 * means only "waiting longer than most" — it drives emphasis, not a deadline.
 */
export function formatDue(hoursUntilDue: number | null): { text: string; overdue: boolean } | null {
  if (hoursUntilDue === null) return null;
  if (hoursUntilDue < 0) {
    const h = Math.abs(hoursUntilDue);
    return { text: h >= 48 ? `waiting ${Math.round(h / 24)}d` : `waiting ${h}h`, overdue: true };
  }
  return { text: 'recent', overdue: false };
}

/** "3h" / "2d" / "just now" — compact age for a timestamp. */
export function formatAge(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function formatMoney(value: number): string {
  if (value >= 10_000) return `$${(value / 1000).toFixed(1)}k`;
  return `$${value.toLocaleString()}`;
}

/**
 * A work item's link into the agency's own pages (AG-01 opens an agent's run log) is followed as
 * the agency. Every other link lands inside the practice's app and is an impersonation.
 */
export function isAgencyLink(deepLink: string): boolean {
  return deepLink.startsWith('/agency/');
}
